import React from "react";

export const formatCurrencyAmount = (amount, currency, fallbackFormatter) => {
  if (amount === undefined || amount === null || amount === "") {
    return "-";
  }

  const numeric = Number(amount);
  if (Number.isNaN(numeric)) {
    return String(amount);
  }

  if (!currency || currency === "SGD") {
    return fallbackFormatter(numeric);
  }

  try {
    return numeric.toLocaleString("en-US", {
      style: "currency",
      currency,
    });
  } catch (error) {
    return `${currency} ${numeric.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
};

export const getCurrencySummaries = (statement) => {
  if (
    Array.isArray(statement?.currency_summaries) &&
    statement.currency_summaries.length > 0
  ) {
    return statement.currency_summaries;
  }

  if (Array.isArray(statement?.currencies) && statement.currencies.length > 0) {
    return statement.currencies;
  }

  return [
    {
      currency: statement?.currency_prefix || null,
      total_deposits: statement?.total_deposits,
      total_withdrawals: statement?.total_withdrawals,
      end_month_balance: statement?.end_month_balance,
      lowest_daily_balance: statement?.lowest_daily_balance,
    },
  ];
};

const renderCurrencyLines = (summaries, renderValue) =>
  summaries.map((summary, index) => (
    <div
      key={`${summary?.currency || "base"}-${index}`}
      style={{
        marginTop: index === 0 ? 0 : "4px",
        whiteSpace: "normal",
        wordBreak: "break-word",
      }}
    >
      {summary?.currency ? `${summary.currency}: ` : ""}
      {renderValue(summary)}
    </div>
  ));

export const getLoanKeywordCount = (statement) =>
  statement?.loan_keyword_matches?.count ?? statement?.loan_keyword_count ?? 0;

export const getNegativeBalanceCount = (statement) =>
  statement?.negative_balance_detection?.count ??
  statement?.negative_balance_count ??
  0;

const mobileCardStyle = {
  borderRadius: "18px",
  background: "#fff",
  border: "1px solid rgba(18,48,74,0.08)",
  padding: "14px",
};

const mobileMetricLabelStyle = {
  fontSize: "11px",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  fontWeight: 800,
  color: "#617487",
};

const mobileMetricValueStyle = {
  marginTop: "6px",
  color: "#12304a",
  fontSize: "13px",
  lineHeight: 1.55,
  wordBreak: "break-word",
};

const BankStatementAnalysisPanel = ({
  enabled,
  loading,
  error,
  statementGroups = [],
  isMobile,
  formatAmount,
  formatDate,
  unavailableMessage = "Bank statement analysis is not available for this request.",
  emptyMessage = "No bank statement analysis available.",
}) => {
  return (
    <div
      style={{
        padding: isMobile ? "10px" : "15px",
        borderRadius: isMobile ? "18px" : "8px",
        width: "100%",
        background: "rgba(14, 90, 165, 0.1)",
        display: "flex",
        flexWrap: "wrap",
        gap: isMobile ? "12px" : "2px 20px",
      }}
    >
      {!enabled ? (
        <div className="profile-viewer__analysis-muted">
          {unavailableMessage}
        </div>
      ) : loading ? (
        <div className="profile-viewer__analysis-muted">
          Loading analysis...
        </div>
      ) : error ? (
        <div className="profile-viewer__analysis-error">{error}</div>
      ) : statementGroups.length === 0 ? (
        <div className="profile-viewer__analysis-muted">{emptyMessage}</div>
      ) : (
        statementGroups.map((group, groupIndex) => {
          const statements = Array.isArray(group?.statements)
            ? group.statements
            : [];

          return (
            <div
              className="profile-viewer__section"
              key={`statement-group-${group?.groupKey || groupIndex}`}
              style={{ width: "100%" }}
            >
              <div className="profile-viewer__section-title">
                {group?.bank || "Bank"}
              </div>
              <div className="profile-viewer__analysis-meta">
                Account: {group?.accountNumber || "-"}
              </div>
              {isMobile ? (
                <div style={{ marginTop: "12px", display: "grid", gap: "12px" }}>
                  {statements.length === 0 ? (
                    <div style={mobileCardStyle}>
                      <div style={{ color: "rgba(0,0,0,0.55)", fontSize: "13px" }}>
                        No statement rows available for this account.
                      </div>
                    </div>
                  ) : (
                    statements.map((statement, index) => {
                      const currencySummaries = getCurrencySummaries(statement);

                      return (
                        <div
                          key={`statement-${group?.groupKey || groupIndex}-${
                            statement?.job_id || index
                          }`}
                          style={mobileCardStyle}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: "12px",
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <div>
                              <div style={mobileMetricLabelStyle}>Month</div>
                              <div
                                style={{
                                  marginTop: "4px",
                                  fontWeight: 800,
                                  color: "#12304a",
                                  fontSize: "15px",
                                }}
                              >
                                {statement?.statement_month_year || "-"}
                              </div>
                            </div>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                              <span
                                style={{
                                  padding: "6px 10px",
                                  borderRadius: "999px",
                                  background: "rgba(110, 64, 201, 0.08)",
                                  color: "#6e40c9",
                                  fontSize: "12px",
                                  fontWeight: 800,
                                }}
                              >
                                Loan keywords: {getLoanKeywordCount(statement)}
                              </span>
                              <span
                                style={{
                                  padding: "6px 10px",
                                  borderRadius: "999px",
                                  background: "rgba(196, 123, 13, 0.12)",
                                  color: "#9c6500",
                                  fontSize: "12px",
                                  fontWeight: 800,
                                }}
                              >
                                Negative bal.: {getNegativeBalanceCount(statement)}
                              </span>
                            </div>
                          </div>

                          <div
                            style={{
                              marginTop: "12px",
                              display: "grid",
                              gridTemplateColumns: "1fr",
                              gap: "12px",
                            }}
                          >
                            <div>
                              <div style={mobileMetricLabelStyle}>Total Deposits</div>
                              <div style={mobileMetricValueStyle}>
                                {renderCurrencyLines(currencySummaries, (summary) =>
                                  formatCurrencyAmount(
                                    summary?.total_deposits,
                                    summary?.currency,
                                    formatAmount,
                                  ),
                                )}
                              </div>
                            </div>
                            <div>
                              <div style={mobileMetricLabelStyle}>Total Withdrawals</div>
                              <div style={mobileMetricValueStyle}>
                                {renderCurrencyLines(currencySummaries, (summary) =>
                                  formatCurrencyAmount(
                                    summary?.total_withdrawals,
                                    summary?.currency,
                                    formatAmount,
                                  ),
                                )}
                              </div>
                            </div>
                            <div>
                              <div style={mobileMetricLabelStyle}>End Month Balance</div>
                              <div style={mobileMetricValueStyle}>
                                {renderCurrencyLines(currencySummaries, (summary) =>
                                  formatCurrencyAmount(
                                    summary?.end_month_balance,
                                    summary?.currency,
                                    formatAmount,
                                  ),
                                )}
                              </div>
                            </div>
                            <div>
                              <div style={mobileMetricLabelStyle}>Lowest Daily Balance</div>
                              <div style={mobileMetricValueStyle}>
                                {renderCurrencyLines(currencySummaries, (summary) => {
                                  const formattedAmount = formatCurrencyAmount(
                                    summary?.lowest_daily_balance?.amount,
                                    summary?.currency,
                                    formatAmount,
                                  );
                                  const formattedDate = summary?.lowest_daily_balance?.date
                                    ? formatDate(summary.lowest_daily_balance.date)
                                    : null;

                                  return formattedDate
                                    ? `${formattedAmount} (${formattedDate})`
                                    : formattedAmount;
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                <table
                  style={{
                    width: "100%",
                    marginTop: "10px",
                    borderCollapse: "collapse",
                    fontSize: "12px",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "8px",
                          borderBottom: "1px solid rgba(0,0,0,0.15)",
                        }}
                      >
                        Month
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "8px",
                          borderBottom: "1px solid rgba(0,0,0,0.15)",
                        }}
                      >
                        Total Deposits
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "8px",
                          borderBottom: "1px solid rgba(0,0,0,0.15)",
                        }}
                      >
                        Total Withdrawals
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "8px",
                          borderBottom: "1px solid rgba(0,0,0,0.15)",
                        }}
                      >
                        End Month Balance
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "8px",
                          borderBottom: "1px solid rgba(0,0,0,0.15)",
                        }}
                      >
                        Lowest Daily Balance
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "8px",
                          borderBottom: "1px solid rgba(0,0,0,0.15)",
                        }}
                      >
                        Loan Keywords
                      </th>
                      <th
                        style={{
                          textAlign: "left",
                          padding: "8px",
                          borderBottom: "1px solid rgba(0,0,0,0.15)",
                        }}
                      >
                        Negative Bal.
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {statements.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          style={{
                            padding: "12px 8px",
                            color: "rgba(0,0,0,0.55)",
                          }}
                        >
                          No statement rows available for this account.
                        </td>
                      </tr>
                    ) : (
                      statements.map((statement, index) => {
                        const currencySummaries = getCurrencySummaries(statement);

                        return (
                          <tr
                            key={`statement-${group?.groupKey || groupIndex}-${
                              statement?.job_id || index
                            }`}
                          >
                            <td
                              style={{
                                padding: "8px",
                                borderTop:
                                  index === 0
                                    ? "none"
                                    : "1px solid rgba(0,0,0,0.08)",
                                verticalAlign: "top",
                              }}
                            >
                              {statement?.statement_month_year || "-"}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderTop:
                                  index === 0
                                    ? "none"
                                    : "1px solid rgba(0,0,0,0.08)",
                                verticalAlign: "top",
                              }}
                            >
                              {renderCurrencyLines(currencySummaries, (summary) =>
                                formatCurrencyAmount(
                                  summary?.total_deposits,
                                  summary?.currency,
                                  formatAmount,
                                ),
                              )}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderTop:
                                  index === 0
                                    ? "none"
                                    : "1px solid rgba(0,0,0,0.08)",
                                verticalAlign: "top",
                              }}
                            >
                              {renderCurrencyLines(currencySummaries, (summary) =>
                                formatCurrencyAmount(
                                  summary?.total_withdrawals,
                                  summary?.currency,
                                  formatAmount,
                                ),
                              )}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderTop:
                                  index === 0
                                    ? "none"
                                    : "1px solid rgba(0,0,0,0.08)",
                                verticalAlign: "top",
                              }}
                            >
                              {renderCurrencyLines(currencySummaries, (summary) =>
                                formatCurrencyAmount(
                                  summary?.end_month_balance,
                                  summary?.currency,
                                  formatAmount,
                                ),
                              )}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderTop:
                                  index === 0
                                    ? "none"
                                    : "1px solid rgba(0,0,0,0.08)",
                                verticalAlign: "top",
                              }}
                            >
                              {renderCurrencyLines(currencySummaries, (summary) => {
                                const formattedAmount = formatCurrencyAmount(
                                  summary?.lowest_daily_balance?.amount,
                                  summary?.currency,
                                  formatAmount,
                                );
                                const formattedDate = summary?.lowest_daily_balance?.date
                                  ? formatDate(summary.lowest_daily_balance.date)
                                  : null;

                                return formattedDate
                                  ? `${formattedAmount} (${formattedDate})`
                                  : formattedAmount;
                              })}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderTop:
                                  index === 0
                                    ? "none"
                                    : "1px solid rgba(0,0,0,0.08)",
                                verticalAlign: "top",
                              }}
                            >
                              {getLoanKeywordCount(statement)}
                            </td>
                            <td
                              style={{
                                padding: "8px",
                                borderTop:
                                  index === 0
                                    ? "none"
                                    : "1px solid rgba(0,0,0,0.08)",
                                verticalAlign: "top",
                              }}
                            >
                              {getNegativeBalanceCount(statement)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default BankStatementAnalysisPanel;
